/**
 * MUS-651 - the phrases a tutorial's staff exercises play, downloaded one by
 * one.
 *
 * They used to be downloaded as one batch that failed as a whole: one phrase
 * that did not download left every exercise of the tutorial without its
 * phrases. Each phrase is now downloaded on its own and tried again before it
 * fails, and only the chapter that needs a failed phrase waits for it.
 */

import axios from 'axios';
import type { ObjectiveData } from 'Types';
import Phrase from 'Models/Phrase';
import MusicXML from 'Models/MusicXML';
import TimeSignature from 'Models/TimeSignature';
import type { CursorData } from 'Phaser/GameObjects/StaffLine/Types';
import { isRetryableRequestFailure, retryWithBackoff } from 'Utils/retryWithBackoff';

export type TutorialPhrase = {
  cursorData: CursorData;
  uuid: string;
  phrase: Phrase;
  timeSignature: TimeSignature;
};

/** The phrases by chapter, then by objective. */
export type TutorialPhrases = TutorialPhrase[][];

export type DownloadedPhrase = { objective: ObjectiveData; phrase: TutorialPhrase };

/** How long one phrase file may take to download before the try fails. */
export const PHRASE_REQUEST_TIMEOUT_MS = 15000;

/** The waits before each new try of a phrase that did not download. */
export const PHRASE_DOWNLOAD_RETRY_DELAYS_MS: readonly number[] = [1000, 3000];

/** Download one objective's phrase: its cursor data and its MusicXML. */
export async function downloadTutorialPhrase({ phrase }: ObjectiveData): Promise<TutorialPhrase> {
  const requestConfig = { timeout: PHRASE_REQUEST_TIMEOUT_MS };
  const cursorData = (
    await axios.get(phrase.music_xml_url_pointers.json_data_url_1440, requestConfig)
  ).data as CursorData;
  const musicXml = (await axios.get(phrase.s3_music_xml_url, requestConfig)).data;
  const timeSignature = new TimeSignature(
    phrase.time_signature_numerator,
    phrase.time_signature_denominator,
  );
  return {
    cursorData,
    uuid: '',
    phrase: new Phrase(
      phrase.s3_music_xml_id,
      new MusicXML(musicXml, [timeSignature]),
      -1,
      '',
      '',
      cursorData.serializedIterator,
      cursorData.serializedIterator,
      cursorData.measureRects,
    ),
    timeSignature,
  };
}

/**
 * Download each objective's phrase on its own, trying each again after every
 * delay in `retryDelaysMs` before it fails (not after a 4xx, which the same
 * request gets again). Each phrase is handed to `onDownloaded` as soon as it is
 * in, so a chapter never waits for another chapter's phrases. Resolves, once
 * every phrase has downloaded or failed, with the objectives whose phrase failed.
 */
export async function downloadTutorialPhrases(
  objectives: readonly ObjectiveData[],
  retryDelaysMs: readonly number[],
  isStale: () => boolean,
  onDownloaded: (downloaded: DownloadedPhrase) => void,
  download: (objective: ObjectiveData) => Promise<TutorialPhrase> = downloadTutorialPhrase,
): Promise<ObjectiveData[]> {
  const outcomes = await Promise.all(
    objectives.map((objective) =>
      retryWithBackoff(() => download(objective), retryDelaysMs, isStale, isRetryableRequestFailure)
        .then((phrase) => {
          if (!isStale()) onDownloaded({ objective, phrase });
          return undefined;
        })
        .catch(() => objective),
    ),
  );
  return outcomes.filter((failed): failed is ObjectiveData => failed !== undefined);
}

/** The phrases with the downloaded ones added, each at its chapter and objective. */
export function withDownloadedPhrases(
  phrases: TutorialPhrases,
  downloaded: readonly DownloadedPhrase[],
): TutorialPhrases {
  return downloaded.reduce<TutorialPhrases>((next, { objective, phrase }) => {
    const chapterPhrases = [...(next[objective.chapter] ?? [])];
    chapterPhrases[objective.objective] = phrase;
    const withChapter = [...next];
    withChapter[objective.chapter] = chapterPhrases;
    return withChapter;
  }, phrases);
}

/** Whether a chapter needs any of these objectives' phrases. */
export const chapterNeedsAny = (
  objectives: readonly ObjectiveData[],
  chapter: number | undefined,
): boolean => objectives.some((objective) => objective.chapter === (chapter ?? 0));

/** Whether every phrase a chapter needs is in (a chapter that needs none has none in). */
export const chapterPhrasesIn = (
  phrases: TutorialPhrases,
  objectives: readonly ObjectiveData[],
  chapter: number,
): boolean => {
  const needed = objectives.filter((objective) => objective.chapter === chapter);
  return (
    needed.length > 0 &&
    needed.every((objective) => phrases[chapter]?.[objective.objective] !== undefined)
  );
};

/**
 * Whether a chapter is still waiting for phrases: it needs some, not all are in,
 * and none of the missing ones has failed.
 */
export const chapterPhrasesPending = (
  phrases: TutorialPhrases,
  objectives: readonly ObjectiveData[],
  failed: readonly ObjectiveData[],
  chapter: number,
): boolean =>
  objectives.some((objective) => objective.chapter === chapter) &&
  !chapterPhrasesIn(phrases, objectives, chapter) &&
  !chapterNeedsAny(failed, chapter);
